import React, { useEffect, useState } from 'react';
import { t } from '../i18n';
import { listenSites, addSite, listenSubmissions, approveSubmission, rejectSubmission, onAuthChange, signOutAdmin, addTaskToSite, updateTaskInSite, removeTaskFromSite, listenSiteTasks, deleteSite, updateSite } from '../firebase';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import Scoreboard from '../components/Scoreboard';
import { useNavigate } from 'react-router-dom';

export default function AdminDashboard() {
  const [editSiteModal, setEditSiteModal] = useState(false);
  const [editSiteId, setEditSiteId] = useState(null);
  const [editSiteTitle, setEditSiteTitle] = useState('');
  const [editSiteDesc, setEditSiteDesc] = useState('');
  const [editSiteLat, setEditSiteLat] = useState('');
  const [editSiteLng, setEditSiteLng] = useState('');

  function openEditSiteModal(site) {
    setEditSiteId(site.id);
    setEditSiteTitle(site.title || '');
    setEditSiteDesc(site.description || '');
    setEditSiteLat(site.location?.lat ? String(site.location.lat) : '');
    setEditSiteLng(site.location?.lng ? String(site.location.lng) : '');
    setEditSiteModal(true);
  }

  async function handleEditSite(e) {
    e.preventDefault();
    if (!editSiteId || !editSiteTitle || !editSiteLat || !editSiteLng) return;
    await updateSite(editSiteId, {
      title: editSiteTitle,
      description: editSiteDesc,
      location: { lat: parseFloat(editSiteLat), lng: parseFloat(editSiteLng) }
    });
    setEditSiteModal(false);
  }
  // Fix leaflet default icon path when using CRA
  try {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
      iconUrl: require('leaflet/dist/images/marker-icon.png'),
      shadowUrl: require('leaflet/dist/images/marker-shadow.png')
    });
  } catch (e) {
    // ignore in environments that disallow require
  }
  const [sites, setSites] = useState([]);
  const [title, setTitle] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [desc, setDesc] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [tasksMap, setTasksMap] = useState({});
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [selectedSiteTasks, setSelectedSiteTasks] = useState([]);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskPoints, setTaskPoints] = useState('10');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskTitle, setEditingTaskTitle] = useState('');
  const [editingTaskDesc, setEditingTaskDesc] = useState('');
  const [taskType, setTaskType] = useState('text');
  const [editingTaskType, setEditingTaskType] = useState('text');
  const [editingTaskPoints, setEditingTaskPoints] = useState('10');
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const unsub = listenSites(setSites);
    const unsub2 = listenSubmissions(setSubmissions);
    const unsubAuth = onAuthChange(user => {
      if (!user) {
        navigate('/admin/login');
      }
    });
    // keep tasks for each site in a map for quick lookup in submissions list
    return () => { unsub(); unsub2(); unsubAuth(); };
  }, [navigate]);

  // listen tasks for all sites and keep in tasksMap
  useEffect(()=>{
    // cleanup previous listeners
    let unsubs = [];
    if (!sites || sites.length === 0) {
      setTasksMap({});
      return () => {};
    }

    sites.forEach(s => {
      const unsub = listenSiteTasks(s.id, (tasks) => {
        setTasksMap(prev => ({ ...prev, [s.id]: tasks }));
      });
      unsubs.push(unsub);
    });

    return () => unsubs.forEach(u=>u && u());
  }, [sites]);

  useEffect(()=>{
    if (!selectedSiteId) {
      setSelectedSiteTasks([]);
      return () => {};
    }
    const unsub = listenSiteTasks(selectedSiteId, setSelectedSiteTasks);
    return () => unsub();
  }, [selectedSiteId]);

  async function handleAddSite(e) {
    e.preventDefault();
    if (!title || !lat || !lng) return;
    await addSite({ title, description: desc, location: { lat: parseFloat(lat), lng: parseFloat(lng) }, tasks: [] });
    setTitle(''); setLat(''); setLng(''); setDesc('');
    setShowSiteModal(false);
  }
  async function handleDeleteSite(siteId) {
    if (!window.confirm('Are you sure you want to delete this site?')) return;
    await deleteSite(siteId);
  }

  async function handleApprove(subId) {
    await approveSubmission(subId);
  }

  async function handleReject(subId) {
    if (!window.confirm('Hylkää lähetys ja vähennä 5 pistettä?')) return;
    await rejectSubmission(subId, 5);
  }

  async function handleSignOut() {
    await signOutAdmin();
    navigate('/admin/login');
  }

  function openTasksModal(siteId) {
    setSelectedSiteId(siteId);
    setShowTasksModal(true);
  }
  function closeTasksModal() {
    setShowTasksModal(false);
    setSelectedSiteId(null);
  }

  return (
  <div className="container py-3">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
        <h2 className="fw-bold text-center text-md-start mb-2 mb-md-0">{t('adminDashboard')}</h2>
        <div className="d-flex flex-row gap-2 justify-content-center">
          <button className="btn btn-secondary" style={{minWidth:120}} onClick={handleSignOut}><i className="bi bi-box-arrow-right"></i> {t('signOut')}</button>
        </div>
      </div>

  <div className="row gy-3 gx-2">
  <div className="col-12 col-md-7">
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title fw-bold">{t('sites')} <span className="badge bg-secondary ms-2">{sites.length}</span></h5>
              <div className="mb-3">
                <button className="btn btn-primary" onClick={()=>setShowSiteModal(true)}><i className="bi bi-plus-circle"></i> {t('addSite')}</button>
              </div>
              <ul className="list-group mb-2">
                {sites.map(s => (
                  <li key={s.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold">{s.title}</div>
                      <div className="small text-muted">{s.description}</div>
                      <div className="small text-muted">lat: {s.location?.lat}, lng: {s.location?.lng}</div>
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-primary" onClick={() => openTasksModal(s.id)}><i className="bi bi-list-task"></i> {t('tasks')}</button>
                      <button className="btn btn-sm btn-warning" onClick={()=>openEditSiteModal(s)}><i className="bi bi-pencil"></i> {t('editSite')}</button>
                      <button className="btn btn-sm btn-danger" onClick={()=>handleDeleteSite(s.id)}><i className="bi bi-trash"></i> {t('remove')}</button>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Modal for adding site */}
              {/* Modal for editing site */}
              {editSiteModal && (
                <div className="modal fade show" style={{display:'block'}} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">{t('editSite')}</h5>
                        <button type="button" className="btn-close" onClick={()=>setEditSiteModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <form onSubmit={handleEditSite} className="row g-2">
                          <div className="col-12 col-sm-6 col-md-4">
                            <input className="form-control mb-2" placeholder={t('siteTitle')} value={editSiteTitle} onChange={e=>setEditSiteTitle(e.target.value)} required />
                          </div>
                          <div className="col-6 col-md-2">
                            <input className="form-control mb-2" placeholder={t('siteLat')} value={editSiteLat} onChange={e=>setEditSiteLat(e.target.value)} required />
                          </div>
                          <div className="col-6 col-md-2">
                            <input className="form-control mb-2" placeholder={t('siteLng')} value={editSiteLng} onChange={e=>setEditSiteLng(e.target.value)} required />
                          </div>
                          <div className="col-12 col-md-4">
                            <input className="form-control mb-2" placeholder={t('siteDescription')} value={editSiteDesc} onChange={e=>setEditSiteDesc(e.target.value)} required />
                          </div>
                          <div className="col-12">
                            <button className="btn btn-primary w-100 mt-2" type="submit">{t('save')}</button>
                          </div>
                        </form>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={()=>setEditSiteModal(false)}>{t('close')}</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Modal backdrop */}
              {editSiteModal && <div className="modal-backdrop fade show"></div>}
              {showSiteModal && (
                <div className="modal fade show" style={{display:'block'}} tabIndex={-1}>
                  <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">{t('addSite')}</h5>
                        <button type="button" className="btn-close" onClick={()=>setShowSiteModal(false)}></button>
                      </div>
                      <div className="modal-body">
                        <form onSubmit={handleAddSite} className="row g-2">
                          <div className="col-12 col-sm-6 col-md-4">
                            <input className="form-control mb-2" placeholder={t('siteTitle')} value={title} onChange={e=>setTitle(e.target.value)} required />
                          </div>
                          <div className="col-6 col-md-2">
                            <input className="form-control mb-2" placeholder={t('siteLat')} value={lat} onChange={e=>setLat(e.target.value)} required />
                          </div>
                          <div className="col-6 col-md-2">
                            <input className="form-control mb-2" placeholder={t('siteLng')} value={lng} onChange={e=>setLng(e.target.value)} required />
                          </div>
                          <div className="col-12 col-md-4">
                            <input className="form-control mb-2" placeholder={t('siteDescription')} value={desc} onChange={e=>setDesc(e.target.value)} required />
                          </div>
                          <div className="col-12">
                            <button className="btn btn-primary w-100 mt-2" type="submit">{t('addSite')}</button>
                          </div>
                        </form>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={()=>setShowSiteModal(false)}>{t('close')}</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Modal backdrop */}
              {showSiteModal && <div className="modal-backdrop fade show"></div>}
            </div>
          </div>
        </div>

  <div className="col-12 col-md-5">
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title fw-bold">{t('pendingSubmissions')} <span className="badge bg-warning text-dark ms-2">{submissions.filter(s=>s.status==='pending').length}</span></h5>
              <ul className="list-group">
                {submissions.filter(s=>s.status==='pending').map(s => {
                  const taskObj = tasksMap[s.siteId]?.find(t => t.id === s.taskId);
                  const pts = taskObj?.points ?? 10;
                  return (
                    <li key={s.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{t('team')}:</strong> {s.teamId}
                          <div><strong>{t('site')}:</strong> { (sites.find(si=>si.id===s.siteId)?.title) || s.siteId }</div>
                          <div><strong>{t('task')}:</strong> { (taskObj?.title) || s.taskId } <span className="badge bg-info text-dark ms-2">{pts} pts</span></div>
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <button className="btn btn-sm btn-primary" onClick={()=>handleApprove(s.id)}><i className="bi bi-check-circle"></i> {t('approve')} (+{pts})</button>
                          <button className="btn btn-sm btn-danger" onClick={()=>handleReject(s.id)}><i className="bi bi-x-circle"></i> {t('reject')} (-5)</button>
                        </div>
                      </div>
                      <div className="mt-2">
                        <strong>{t('proof')}:</strong>
                        <div>
                          {s.payload?.imageUrl ? (
                            <img src={s.payload.imageUrl} alt="proof" className="img-fluid mt-2 rounded border" style={{maxWidth:180}} />
                          ) : (
                            <pre className="mt-2" style={{whiteSpace:'pre-wrap'}}>{s.payload?.text || JSON.stringify(s.payload)}</pre>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h5 className="card-title fw-bold">{t('liveScoreboard')}</h5>
              <Scoreboard />
            </div>
          </div>
        </div>
      </div>

      {/* Modal for task management */}
      {showTasksModal && selectedSiteId && (
        <div className="modal fade show" style={{display:'block'}} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('manageTasks')} <span className="text-primary">{sites.find(s=>s.id===selectedSiteId)?.title}</span></h5>
                <button type="button" className="btn-close" onClick={closeTasksModal}></button>
              </div>
              <div className="modal-body">
                <strong>{t('tasks')}</strong>
                <ul className="list-group mt-2">
                  {(selectedSiteTasks || []).map(taskItem => (
                    <li key={taskItem.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">{taskItem.title} <span className="badge bg-info text-dark ms-2">{taskItem.points ?? 10} pts</span></div>
                        <div className="small text-muted">{taskItem.description}</div>
                      </div>
                      <div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                          setEditingTaskId(taskItem.id);
                          setEditingTaskTitle(taskItem.title || '');
                          setEditingTaskDesc(taskItem.description || '');
                          setEditingTaskType(taskItem.type || 'text');
                          setEditingTaskPoints(taskItem.points != null ? String(taskItem.points) : '10');
                        }}><i className="bi bi-pencil"></i> {t('editTask')}</button>
                        <button className="btn btn-sm btn-danger ms-2" onClick={async ()=>{
                          if(!window.confirm(t('remove') + ' ' + t('task') + '?')) return;
                          await removeTaskFromSite(selectedSiteId, taskItem.id);
                        }}><i className="bi bi-trash"></i> {t('remove')}</button>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Task edit/add forms as before */}
                {editingTaskId ? (
                  <div className="mb-3">
                    <h6>{t('editTask')}</h6>
                    <input className="form-control mb-2" placeholder={t('taskTitle')} value={editingTaskTitle} onChange={e=>setEditingTaskTitle(e.target.value)} />
                    <textarea className="form-control mb-2" placeholder={t('taskDescription')} value={editingTaskDesc} onChange={e=>setEditingTaskDesc(e.target.value)} />
                    <label className="form-label">{t('proofType')}</label>
                    <select className="form-select mb-2" value={editingTaskType} onChange={e=>setEditingTaskType(e.target.value)}>
                      <option value="text">{t('text')}</option>
                      <option value="photo">{t('photo')}</option>
                    </select>
                    <label className="form-label">{t('points')}</label>
                    <input className="form-control mb-2" type="number" min="0" value={editingTaskPoints} onChange={event => setEditingTaskPoints(event.target.value)} />
                    <div className="mt-2">
                      <button className="btn btn-primary me-2" onClick={async ()=>{
                        if(!editingTaskTitle.trim()) return;
                        await updateTaskInSite(selectedSiteId, editingTaskId, { title: editingTaskTitle.trim(), description: editingTaskDesc.trim(), type: editingTaskType, points: editingTaskPoints ? Number(editingTaskPoints) : 10 });
                        setEditingTaskId(null); setEditingTaskTitle(''); setEditingTaskDesc('');
                      }}><i className="bi bi-save"></i> {t('save')}</button>
                      <button className="btn btn-secondary" onClick={()=>{ setEditingTaskId(null); setEditingTaskTitle(''); setEditingTaskDesc(''); }}><i className="bi bi-x-circle"></i> {t('cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <h6>{t('addTask')}</h6>
                    <input className="form-control mb-2" placeholder={t('taskTitle')} value={taskTitle} onChange={e=>setTaskTitle(e.target.value)} />
                    <textarea className="form-control mb-2" placeholder={t('taskDescription')} value={taskDesc} onChange={e=>setTaskDesc(e.target.value)} />
                    <label className="form-label">{t('proofType')}</label>
                    <select className="form-select mb-2" value={taskType} onChange={e=>setTaskType(e.target.value)}>
                      <option value="text">{t('text')}</option>
                      <option value="photo">{t('photo')}</option>
                    </select>
                    <label className="form-label">{t('points')}</label>
                    <input className="form-control mb-2" type="number" min="0" value={taskPoints} onChange={e=>setTaskPoints(e.target.value)} />
                    <button className="btn btn-success mt-2" onClick={async ()=>{
                      if(!taskTitle.trim()) return;
                      await addTaskToSite(selectedSiteId, { title: taskTitle.trim(), description: taskDesc.trim(), type: taskType, points: taskPoints ? Number(taskPoints) : 10 });
                      setTaskTitle(''); setTaskDesc('');
                    }}><i className="bi bi-plus-circle"></i> {t('addTask')}</button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeTasksModal}>{t('close')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showTasksModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
}
